import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { Constants } from '@base/src/app/models/constants';
import { QRIcon } from '@base/src/app/models/qr-icon.model';
import { QRToggle } from 'src/app/models/qr-toggle.model';
import { Subject, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

@Component({
  selector: 'qr-toggle',
  templateUrl: './toggle.component.html',
  styleUrls: ['./toggle.component.scss'],
})
export class ToggleComponent implements OnInit, OnDestroy {
  @Input() config!: QRToggle;
  @Input() isDisabled?: boolean = false;
  @Output() change: EventEmitter<number> = new EventEmitter<number>();

  readonly ICON_CALENDAR_EVENT = Constants.ICONS.CALENDAR_EVENT;
  readonly ICON_PENCIL = Constants.ICONS.PENCIL_BLUE;
  readonly ICON_COLOR_GREY_700 = QRIcon.COLOR_GREY_700;
  readonly DEFAULT_CUSTOM_DEBOUNCE_MS = 500;

  iconsColor: Map<number, string> = new Map();
  customInputDisplay = '';

  private readonly customValueSubject = new Subject<string | number>();
  private customValueSubscription?: Subscription;

  ngOnInit(): void {
    this.initCustomValue();
    this.syncCustomInputDisplay();
    this.setupCustomValueDebounce();
    this.setIconsColor();
  }

  ngOnDestroy(): void {
    this.customValueSubscription?.unsubscribe();
  }

  isCustomOption(index: number): boolean {
    return this.config.customOption?.index === index;
  }

  getCustomSuffix(): string {
    return this.config.customOption?.suffix ?? '';
  }

  getCustomPrefix(): string {
    return this.config.customOption?.prefix ?? '';
  }

  getCustomLabel(): string {
    return `${this.getCustomPrefix()}${this.getCustomValueDisplay()}${this.getCustomSuffix()}`;
  }

  getCustomValueDisplay(): string {
    const RAW = this.config.customValue;
    const HAS_VALUE =
      RAW !== undefined && RAW !== null && String(RAW).trim() !== '';
    return HAS_VALUE ? String(RAW) : '';
  }

  hasCustomValue(): boolean {
    const RAW = this.config.customValue;
    return RAW !== undefined && RAW !== null && String(RAW).trim() !== '';
  }

  getCustomInputSize(): number {
    return Math.max(this.customInputDisplay.length, 1);
  }

  getTooltip(index: number): string {
    const FROM_ARRAY = this.config.tooltips?.[index];
    if (FROM_ARRAY) {
      return FROM_ARRAY;
    }
    if (this.isCustomOption(index)) {
      return this.config.customOption?.tooltip ?? '';
    }
    return '';
  }

  setIconsColor(): void {
    for (
      let index: number = 0;
      index < this.config.viewValues.length;
      index++
    ) {
      let color: string = QRIcon.COLOR_WHITE;
      if (!this.isDisabled) {
        switch (this.config.hierarchy) {
          case QRToggle.HIERARCHY_1:
            color =
              this.config.optionSelected == index
                ? QRIcon.COLOR_WHITE
                : QRIcon.COLOR_PRIMARY_700;
            break;
          case QRToggle.HIERARCHY_2:
            color =
              this.config.optionSelected == index
                ? QRIcon.COLOR_PRIMARY_700
                : QRIcon.COLOR_GREY_700;
            break;
          case QRToggle.HIERARCHY_3:
            color = QRIcon.COLOR_GREY_700;
            break;
        }
        this.iconsColor.set(index, color);
      } else {
        this.setIconsColorDisabled(index);
      }
    }
  }

  setIconsColorDisabled(index: number): void {
    let color: string = QRIcon.COLOR_WHITE;
    switch (this.config.hierarchy) {
      case QRToggle.HIERARCHY_1:
        color = QRIcon.COLOR_WHITE;
        break;
      case QRToggle.HIERARCHY_2:
        color =
          this.config.optionSelected == index
            ? QRIcon.COLOR_WHITE
            : QRIcon.COLOR_GREY_300;
        break;
      case QRToggle.HIERARCHY_3:
        color = QRIcon.COLOR_GREY_300;
        break;
    }
    this.iconsColor.set(index, color);
  }

  select(id: number): void {
    if (!this.isDisabled) {
      this.config.optionSelected = id;
      this.setIconsColor();
      this.change.emit(this.config.optionSelected);
    }
  }

  onCustomInputChange(raw: string): void {
    if (this.isDisabled) {
      return;
    }
    this.applyCustomNumericValue(raw);
  }

  onCustomInputKeydown(event: KeyboardEvent): void {
    if (this.isDisabled) {
      return;
    }
    const KEY = event.key;
    const ALLOWED_NAVIGATION_KEYS = [
      'Backspace',
      'Delete',
      'Tab',
      'ArrowLeft',
      'ArrowRight',
      'Home',
      'End',
    ];

    if (ALLOWED_NAVIGATION_KEYS.includes(KEY) || event.ctrlKey || event.metaKey) {
      return;
    }

    if (!/^\d$/.test(KEY)) {
      event.preventDefault();
    }
  }

  onCustomInputPaste(event: ClipboardEvent): void {
    if (this.isDisabled) {
      return;
    }
    event.preventDefault();
    const PASTED = event.clipboardData?.getData('text') ?? '';
    this.applyCustomNumericValue(PASTED);
  }

  stopInputEvent(event: Event): void {
    event.stopPropagation();
  }

  private applyCustomNumericValue(raw: string): void {
    const FILTERED = raw.replace(/\D/g, '');
    this.customInputDisplay = FILTERED;
    this.config.customValue = FILTERED === '' ? '' : Number(FILTERED);
    this.customValueSubject.next(this.config.customValue);
  }

  private initCustomValue(): void {
    if (!this.config.customOption) {
      return;
    }
    if (
      this.config.customValue === undefined ||
      this.config.customValue === null
    ) {
      const DEFAULT = this.config.customOption.defaultValue;
      this.config.customValue =
        DEFAULT !== undefined && DEFAULT !== null ? DEFAULT : '';
    }
  }

  private syncCustomInputDisplay(): void {
    if (!this.config.customOption) {
      return;
    }
    const RAW = this.config.customValue;
    this.customInputDisplay =
      RAW !== undefined && RAW !== null && String(RAW) !== ''
        ? String(RAW)
        : '';
  }

  private setupCustomValueDebounce(): void {
    if (!this.config.customOption) {
      return;
    }
    const DEBOUNCE_MS =
      this.config.customOption.debounceTime ?? this.DEFAULT_CUSTOM_DEBOUNCE_MS;
    this.customValueSubscription = this.customValueSubject
      .pipe(debounceTime(DEBOUNCE_MS))
      .subscribe((value) => {
        const MATCHING_INDEX = this.findMatchingStaticOptionIndex(value);
        if (MATCHING_INDEX !== null) {
          this.clearCustomValue();
          this.select(MATCHING_INDEX);
          return;
        }
        if (value === '' || value === null || value === undefined) {
          return;
        }
        this.select(this.config.customOption!.index);
      });
  }

  private findMatchingStaticOptionIndex(
    value: string | number,
  ): number | null {
    const CUSTOM_INDEX = this.config.customOption?.index;
    if (CUSTOM_INDEX === undefined || value === '' || value === null) {
      return null;
    }

    const NUMERIC_VALUE = Number(value);
    if (Number.isNaN(NUMERIC_VALUE)) {
      return null;
    }

    for (let index = 0; index < this.config.viewValues.length; index++) {
      if (index === CUSTOM_INDEX) {
        continue;
      }
      const LABEL_VALUE = this.extractNumericFromLabel(
        this.config.viewValues[index],
      );
      if (LABEL_VALUE !== null && LABEL_VALUE === NUMERIC_VALUE) {
        return index;
      }
    }

    return null;
  }

  private extractNumericFromLabel(label: string): number | null {
    const MATCH = label.match(/^(\d+)/);
    if (!MATCH) {
      return null;
    }
    const PARSED = Number(MATCH[1]);
    return Number.isNaN(PARSED) ? null : PARSED;
  }

  private clearCustomValue(): void {
    this.config.customValue = '';
    this.customInputDisplay = '';
  }
}
